import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { useI18n } from "@/context/I18nContext";
import { LANGUAGES } from "@/lib/i18n";
import { can, ROLE_LABELS } from "@/lib/permissions";
import { initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Menu, Search, Plus, Bell, Sun, Moon, Globe, LogOut, Users, FileText, CheckSquare,
} from "lucide-react";

export function Header({ onMenu }) {
  const { user, tenant, logout } = useAuth();
  const { dark, toggleTheme } = useBranding();
  const { lang, setLang } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState(false);
  const [q, setQ] = useState("");

  const { data: searchData } = useQuery({
    queryKey: ["search", q],
    queryFn: async () => (await api.get(`/customers?q=${encodeURIComponent(q)}&limit=6`)).data,
    enabled: search && q.length > 1,
  });

  const { data: notif } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/communication/notifications")).data,
    refetchInterval: 60000,
  });

  const markRead = async (id) => {
    await api.post(`/communication/notifications/${id}/read`);
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-card/90 px-3 backdrop-blur-md sm:px-4">
      <button onClick={onMenu} data-testid="mobile-menu-button"
        className="rounded-md p-2 text-muted-foreground hover:bg-muted lg:hidden">
        <Menu className="h-5 w-5" />
      </button>

      <button onClick={() => setSearch(true)} data-testid="global-search-trigger"
        className="group flex h-9 flex-1 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm text-muted-foreground hover:border-accent/40 sm:max-w-sm">
        <Search className="h-4 w-4" />
        <span className="hidden sm:inline">Kunden suchen…</span>
        <kbd className="ml-auto hidden rounded border border-border px-1.5 py-0.5 text-[10px] font-medium sm:inline">Strg K</kbd>
      </button>

      <div className="flex-1 lg:hidden" />

      <div className="flex items-center gap-1">
        {/* Quick create */}
        {(can(user?.role, "customers:write") || can(user?.role, "contracts:write")) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="quick-create-button">
                <Plus className="h-4 w-4" /><span className="hidden sm:inline">Neu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => navigate("/kunden?neu=1")} data-testid="quick-create-customer">
                <Users className="mr-2 h-4 w-4" /> Neuer Kunde
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/vertraege?neu=1")} data-testid="quick-create-contract">
                <FileText className="mr-2 h-4 w-4" /> Neuer Vertrag
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/aufgaben?neu=1")} data-testid="quick-create-task">
                <CheckSquare className="mr-2 h-4 w-4" /> Neue Aufgabe
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Language */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="language-toggle">
              <Globe className="h-[18px] w-[18px]" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {LANGUAGES.map((l) => (
              <DropdownMenuItem key={l.key} onClick={() => setLang(l.key)} data-testid={`lang-${l.key}`}>
                {l.label} {lang === l.key && "✓"}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative h-9 w-9" data-testid="notifications-button">
              <Bell className="h-[18px] w-[18px]" />
              {notif?.unread > 0 && (
                <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-destructive-foreground">
                  {notif.unread}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel>Benachrichtigungen</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {(notif?.items || []).length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">Keine Benachrichtigungen</div>
            )}
            {(notif?.items || []).slice(0, 8).map((n) => (
              <DropdownMenuItem key={n.id} onClick={() => markRead(n.id)} className="flex-col items-start gap-0.5">
                <div className="flex w-full items-center gap-2">
                  {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-accent" />}
                  <span className="text-xs font-semibold">{n.title}</span>
                </div>
                <span className="text-[11px] text-muted-foreground">{n.message}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Theme */}
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={toggleTheme} data-testid="theme-toggle">
          {dark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </Button>

        {/* Profile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-1 flex items-center gap-2 rounded-md p-1 hover:bg-muted" data-testid="profile-menu-button">
              <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary text-xs text-primary-foreground">{initials(user?.name)}</AvatarFallback></Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <div className="px-2 py-1.5">
              <div className="text-sm font-semibold">{user?.name}</div>
              <div className="text-xs text-muted-foreground">{user?.email}</div>
              <Badge variant="secondary" className="mt-1.5 text-[10px]">{ROLE_LABELS[user?.role] || user?.role}</Badge>
            </div>
            <DropdownMenuSeparator />
            <div className="px-2 py-1 text-[11px] text-muted-foreground">Organisation</div>
            <div className="px-2 pb-1.5 text-sm font-medium">{tenant?.name}</div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/einstellungen")}>Einstellungen</DropdownMenuItem>
            <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" /> Abmelden
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CommandDialog open={search} onOpenChange={setSearch}>
        <CommandInput placeholder="Kunden nach Name, Nummer, Telefon suchen…" value={q} onValueChange={setQ}
          data-testid="global-search-input" />
        <CommandList>
          <CommandEmpty>Keine Treffer.</CommandEmpty>
          <CommandGroup heading="Kunden">
            {(searchData?.items || []).map((c) => (
              <CommandItem key={c.id} value={c.display_name + c.customer_number}
                onSelect={() => { setSearch(false); navigate(`/kunden/${c.id}`); }}>
                <Users className="mr-2 h-4 w-4" />
                <span>{c.display_name}</span>
                <span className="ml-auto text-xs text-muted-foreground">{c.customer_number}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </header>
  );
}
